import React, { useEffect } from 'react';
import {
  ArrowRight,
  BadgeCheck,
  Banknote,
  BarChart3,
  BookOpenCheck,
  Boxes,
  Building2,
  CheckCircle2,
  ChevronRight,
  ClipboardCheck,
  FileCheck2,
  FileText,
  Gavel,
  GraduationCap,
  Handshake,
  Landmark,
  LayoutDashboard,
  LifeBuoy,
  MessagesSquare,
  MonitorCheck,
  PackageCheck,
  RefreshCw,
  SearchCheck,
  ShieldCheck,
  ShoppingBag,
  Store,
  Target,
  TrendingUp,
  UploadCloud,
  UserCheck,
  Users,
  Video,
} from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';

const GEM_LOGO = '/assets/services/gem-logo.jpg';

const supportAreas = [
  {
    icon: UserCheck,
    title: 'Seller registration & account setup',
    text: 'Complete seller onboarding, organisation-profile preparation and account-readiness support.',
    points: ['KYC and business information', 'Document validation and upload', 'Profile completion', 'Certification guidance'],
  },
  {
    icon: Video,
    title: 'GeM vendor assessment',
    text: 'Assessment applicability and execution support where vendor assessment applies.',
    points: ['Desktop assessment', 'Video assessment coordination', 'NC response support', 'Correction and resubmission'],
  },
  {
    icon: Boxes,
    title: 'Catalogue & listing management',
    text: 'Structure, publish and maintain compliant product and service catalogue information.',
    points: ['Specifications and descriptions', 'Images and certificates', 'Brand and model information', 'Price and stock updates'],
  },
  {
    icon: Gavel,
    title: 'Bid & tender management',
    text: 'Identify relevant procurement opportunities and coordinate technical and commercial participation.',
    points: ['Opportunity screening', 'Eligibility review', 'Bid documentation', 'Submission and status tracking'],
  },
  {
    icon: PackageCheck,
    title: 'GeM order management',
    text: 'Maintain visibility and coordination from purchase order through fulfilment and payment follow-up.',
    points: ['Order acceptance', 'Dispatch coordination', 'Invoice support', 'Buyer communication'],
  },
  {
    icon: RefreshCw,
    title: 'Account compliance & maintenance',
    text: 'Keep seller information, certificates, catalogues and platform requirements current.',
    points: ['Periodic monitoring', 'Profile and document updates', 'Notice support', 'Audit-readiness activities'],
  },
  {
    icon: BarChart3,
    title: 'Reporting & performance analysis',
    text: 'Create structured visibility across bidding, orders, revenue, catalogue work and next actions.',
    points: ['Bid and order status', 'Revenue and activity reporting', 'Key observations', 'Performance insights'],
  },
  {
    icon: GraduationCap,
    title: 'GeM training & advisory',
    text: 'Help sales, commercial, compliance and operations teams manage the GeM ecosystem effectively.',
    points: ['Account operations', 'Tender processes', 'Commercial considerations', 'Internal process design'],
  },
  {
    icon: LifeBuoy,
    title: 'Queries, disputes & after-sales support',
    text: 'Coordinate buyer queries, complaints, helpdesk interactions and issue-resolution records.',
    points: ['Clarification support', 'Order communication', 'Dispute coordination', 'Pending-matter follow-up'],
  },
];

const processSteps = [
  { icon: SearchCheck, number: '01', title: 'Business assessment', text: 'Understand the organisation, offering, target government buyers and expected GeM activity.' },
  { icon: FileCheck2, number: '02', title: 'Registration & documentation', text: 'Coordinate seller registration, KYC, profile completion and supporting documents.' },
  { icon: Video, number: '03', title: 'Vendor assessment', text: 'Support desktop or video assessment and non-conformity closure where applicable.' },
  { icon: UploadCloud, number: '04', title: 'Catalogue development', text: 'Structure and upload relevant product or service offers with supporting information.' },
  { icon: Target, number: '05', title: 'Bid identification', text: 'Screen procurement opportunities against the seller’s offering and eligibility.' },
  { icon: Gavel, number: '06', title: 'Bid participation', text: 'Coordinate technical and commercial inputs, documents and bid submission.' },
  { icon: ShoppingBag, number: '07', title: 'Order management', text: 'Support acceptance, fulfilment, invoicing, timelines and buyer communication.' },
  { icon: MonitorCheck, number: '08', title: 'Ongoing management', text: 'Maintain the account, catalogue, reporting, compliance records and open queries.' },
];

const documentGroups = [
  {
    title: 'Authorised person',
    icon: UserCheck,
    items: ['Aadhaar-linked details', 'PAN and designation', 'Active email and mobile number', 'Authorisation or board resolution, where applicable'],
  },
  {
    title: 'Business entity',
    icon: Building2,
    items: ['Company or organisation documents', 'PAN and GST details', 'CIN or firm information, where applicable', 'MSME / Udyam documentation, where relevant'],
  },
  {
    title: 'Commercial profile',
    icon: Banknote,
    items: ['Bank account and cancelled-cheque details', 'Registered office or business address', 'Financial and statutory documents', 'Bid-specific commercial records'],
  },
  {
    title: 'Offering and eligibility',
    icon: ClipboardCheck,
    items: ['Product or service technical documents', 'Manufacturer / brand authorisation', 'Product and quality certificates', 'Declarations requested for a category or bid'],
  },
];

const engagementModels = [
  {
    icon: BadgeCheck,
    eyebrow: 'One-time engagement',
    title: 'Registration & vendor assessment',
    text: 'For businesses that need structured onboarding and assessment support before operating independently.',
    items: ['Seller registration', 'Organisation profile', 'Documentation', 'Desktop / video assessment', 'NC closure support'],
    tone: 'border-sky-200 bg-gradient-to-br from-white to-sky-50',
  },
  {
    icon: LayoutDashboard,
    eyebrow: 'Monthly engagement',
    title: 'GeM account management',
    text: 'For businesses seeking continuous catalogue, tender, order and compliance coordination.',
    items: ['Account and catalogue management', 'Bid identification and participation', 'Order monitoring', 'Buyer communication', 'Reporting and analytics'],
    tone: 'border-emerald-200 bg-gradient-to-br from-white to-emerald-50',
  },
  {
    icon: TrendingUp,
    eyebrow: 'Flexible engagement',
    title: 'Performance-based / hybrid',
    text: 'A commercial structure that can be discussed for suitable activity levels, categories and opportunity volumes.',
    items: ['Scope-led commercial model', 'Defined opportunity criteria', 'Activity and outcome reporting', 'Portfolio-based planning', 'Periodic performance review'],
    tone: 'border-amber-200 bg-gradient-to-br from-white to-amber-50',
  },
];

const beneficiaries = [
  'Manufacturers', 'Product brands', 'Importers', 'Distributors', 'Service providers', 'MSMEs', 'Start-ups',
  'Technology companies', 'IT & digital providers', 'Furniture manufacturers', 'Electronics companies',
  'Healthcare suppliers', 'Industrial manufacturers', 'Professional service providers',
];

const performanceMetrics = [
  ['Bids identified', Target],
  ['Bids submitted', Gavel],
  ['Orders received', ShoppingBag],
  ['Orders won', BadgeCheck],
  ['Revenue generated', Banknote],
  ['Catalogue activity', Boxes],
  ['Account updates', RefreshCw],
  ['Action points', ClipboardCheck],
];

const faqs = [
  ['Can a business register for both goods and services?', 'Yes. The official seller workflow allows a user to select product seller, service provider or both. The profile and catalogue should be prepared for the selected route.'],
  ['Does GeM registration guarantee orders?', 'No. Registration creates marketplace access. Order opportunities depend on catalogue visibility, buyer requirements, eligibility, pricing, bid participation and performance.'],
  ['Is every document required for every seller?', 'No. Requirements vary by constitution, seller role, category and bid. Portal validations and buyer-specific conditions should be checked for the exact case.'],
  ['Can an authorised reseller participate?', 'Authorised resellers can participate in relevant categories when their role and authorisation evidence satisfy current category and bid requirements.'],
];

const GemServicePage = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    document.title = 'GeM Registration & Account Management Services | Complise India';

    const description = document.querySelector('meta[name="description"]');
    if (description) {
      description.content = 'End-to-end GeM registration, vendor assessment, catalogue, tender, order and account management support for sellers, manufacturers and service providers.';
    }
  }, []);

  return (
    <div className="min-h-screen bg-[#FDFBF7] text-neutral-900">
      <Header />
      <main>
        <section className="relative overflow-hidden border-b border-neutral-200 bg-gradient-to-br from-white via-[#fffdf6] to-[#f4f8ff]">
          <div className="absolute -right-36 -top-32 h-[480px] w-[480px] rounded-full bg-emerald-100/50 blur-3xl" />
          <div className="absolute -bottom-44 left-[35%] h-[380px] w-[380px] rounded-full bg-amber-100/45 blur-3xl" />
          <div className="relative mx-auto grid max-w-[1280px] items-center gap-12 px-6 py-16 lg:grid-cols-[1.08fr_0.92fr] lg:px-10 lg:py-24">
            <div>
              <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.18em] text-emerald-800">
                <Landmark className="h-3.5 w-3.5" /> Government procurement marketplace
              </div>
              <h1 className="max-w-[760px] font-display text-4xl font-extrabold leading-[1.02] tracking-[-0.045em] text-neutral-950 md:text-6xl lg:text-[4.5rem]">
                GeM registration and account management, end to end.
              </h1>
              <p className="mt-6 max-w-[720px] text-base leading-8 text-neutral-600 md:text-lg">
                Complise India supports sellers, manufacturers and service providers through registration, vendor assessment, catalogue management, bid participation, order coordination and ongoing account support on the Government e Marketplace.
              </p>
              <div className="mt-7 flex flex-wrap gap-2">
                {['Seller registration', 'Vendor assessment', 'Bid & tender management', 'Account management'].map((item) => (
                  <span key={item} className="rounded-full border border-neutral-200 bg-white px-3 py-2 text-[11px] font-bold text-neutral-700 shadow-sm">{item}</span>
                ))}
              </div>
              <div className="mt-9 flex flex-col gap-3 sm:flex-row">
                <a href="/contact" className="group inline-flex h-[52px] items-center justify-center gap-3 rounded-full bg-[#C41E3A] px-6 text-sm font-bold text-white shadow-[0_16px_34px_-16px_rgba(196,30,58,0.8)] transition hover:-translate-y-0.5 hover:bg-[#a81830]">
                  Start GeM consultation <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </a>
                <a href="https://gem.gov.in/" target="_blank" rel="noopener noreferrer" className="inline-flex h-[52px] items-center justify-center gap-2 rounded-full border border-neutral-300 bg-white px-6 text-sm font-bold text-neutral-800 transition hover:border-emerald-400 hover:text-emerald-800">
                  Open official GeM portal <ChevronRight className="h-4 w-4" />
                </a>
              </div>
            </div>

            <div className="relative">
              <div className="absolute -inset-5 rounded-[2.5rem] bg-gradient-to-br from-amber-200/40 via-emerald-100/50 to-sky-100/60 blur-2xl" />
              <div className="relative overflow-hidden rounded-[2rem] border border-neutral-200 bg-white p-5 shadow-[0_35px_90px_-42px_rgba(15,23,42,0.42)] md:p-7">
                <div className="rounded-[1.5rem] border border-amber-100 bg-[#fffdf0] p-4">
                  <img src={GEM_LOGO} alt="Government e Marketplace GeM logo" className="h-36 w-full object-contain mix-blend-multiply md:h-44" />
                </div>
                <div className="mt-6 flex items-center justify-between gap-4">
                  <div>
                    <span className="text-[9px] font-extrabold uppercase tracking-[0.18em] text-emerald-700">Seller journey</span>
                    <h2 className="mt-1 font-display text-xl font-extrabold text-neutral-900">One connected procurement workflow</h2>
                  </div>
                  <span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-neutral-950 text-white"><Store className="h-5 w-5" /></span>
                </div>
                <div className="mt-5 grid grid-cols-2 gap-3">
                  {[
                    ['01', 'Register', UserCheck],
                    ['02', 'Assess', Video],
                    ['03', 'Catalogue', Boxes],
                    ['04', 'Manage', LayoutDashboard],
                  ].map(([number, title, Icon]) => (
                    <div key={number} className="group rounded-2xl border border-neutral-200 bg-neutral-50 p-4 transition hover:-translate-y-1 hover:border-emerald-200 hover:bg-emerald-50/60">
                      <div className="flex items-center justify-between"><span className="text-[10px] font-black text-neutral-400">{number}</span><Icon className="h-4 w-4 text-emerald-700" /></div>
                      <strong className="mt-4 block text-sm text-neutral-900">{title}</strong>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-b border-neutral-200 bg-white">
          <div className="mx-auto grid max-w-[1280px] grid-cols-2 gap-px bg-neutral-200 md:grid-cols-4">
            {[
              ['Product sellers', ShoppingBag],
              ['Service providers', Users],
              ['OEMs', ShieldCheck],
              ['Authorised resellers', BadgeCheck],
            ].map(([label, Icon]) => (
              <div key={label} className="flex items-center gap-3 bg-white px-5 py-5"><Icon className="h-5 w-5 text-[#C41E3A]" /><span className="text-xs font-bold text-neutral-700">{label}</span></div>
            ))}
          </div>
        </section>

        <section id="overview" className="mx-auto max-w-[1280px] px-6 py-16 lg:px-10 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-[0.82fr_1.18fr] lg:items-center">
            <div>
              <span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#C41E3A]">What GeM is</span>
              <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight md:text-5xl">A unified digital platform for government procurement.</h2>
              <p className="mt-5 text-sm leading-7 text-neutral-600 md:text-base">
                GeM facilitates end-to-end procurement of goods and services by government departments, organisations and public sector undertakings. For sellers, the operating journey extends beyond account creation into catalogue quality, opportunity assessment, bidding, fulfilment and transaction records.
              </p>
              <a href="https://assets-bg.gem.gov.in/resources/pdf/seller-user-manual.pdf" target="_blank" rel="noopener noreferrer" className="mt-6 inline-flex items-center gap-2 text-sm font-bold text-[#C41E3A]">
                Read the official seller manual <BookOpenCheck className="h-4 w-4" />
              </a>
            </div>
            <div className="relative overflow-hidden rounded-[2rem] bg-[#0b1724] p-6 text-white shadow-2xl md:p-9">
              <div className="absolute -right-20 -top-20 h-64 w-64 rounded-full border-[34px] border-emerald-400/10" />
              <div className="relative flex items-center gap-3"><span className="grid h-12 w-12 place-items-center rounded-2xl bg-emerald-400/15 text-emerald-300"><LayoutDashboard className="h-5 w-5" /></span><div><span className="text-[9px] font-bold uppercase tracking-[0.2em] text-emerald-300">Operating view</span><h3 className="font-display text-xl font-extrabold">From profile to performance</h3></div></div>
              <div className="relative mt-8 grid gap-3 sm:grid-cols-3">
                {[
                  ['Profile', 'Verified organisation and seller role'],
                  ['Opportunity', 'Catalogue, bids and reverse auctions'],
                  ['Execution', 'Orders, delivery and payment records'],
                ].map(([title, text], index) => (
                  <div key={title} className="rounded-2xl border border-white/10 bg-white/[0.06] p-4"><span className="text-[10px] font-black text-emerald-300">0{index + 1}</span><strong className="mt-4 block text-sm">{title}</strong><p className="mt-2 text-[11px] leading-5 text-white/60">{text}</p></div>
                ))}
              </div>
            </div>
          </div>
        </section>

        <section id="support" className="bg-white py-16 lg:py-24">
          <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
            <div className="max-w-3xl">
              <span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#C41E3A]">Scope of support</span>
              <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight md:text-5xl">Nine connected services across the GeM lifecycle.</h2>
              <p className="mt-4 text-neutral-600">From first-time onboarding to ongoing tender, order and account management, each workstream is adapted to the seller, offering, category and activity volume.</p>
            </div>
            <div className="mt-10 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {supportAreas.map(({ icon: Icon, title, text, points }, index) => (
                <article key={title} className="group relative min-h-[335px] overflow-hidden rounded-[1.75rem] border border-neutral-200 bg-gradient-to-br from-white to-neutral-50 p-6 transition duration-300 hover:-translate-y-1.5 hover:border-emerald-200 hover:shadow-[0_24px_60px_-38px_rgba(5,150,105,0.65)]">
                  <span className="absolute right-5 top-3 font-display text-6xl font-black text-neutral-100 transition group-hover:text-emerald-50">0{index + 1}</span>
                  <span className="relative grid h-12 w-12 place-items-center rounded-2xl bg-emerald-50 text-emerald-700 transition group-hover:bg-emerald-700 group-hover:text-white"><Icon className="h-5 w-5" /></span>
                  <h3 className="relative mt-8 font-display text-xl font-extrabold">{title}</h3>
                  <p className="relative mt-3 text-sm leading-6 text-neutral-600">{text}</p>
                  <div className="relative mt-5 grid gap-2">
                    {points.map((point) => <span key={point} className="flex items-start gap-2 text-[11px] font-semibold leading-5 text-neutral-600"><CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" />{point}</span>)}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="documents" className="mx-auto max-w-[1280px] px-6 py-16 lg:px-10 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-[0.72fr_1.28fr]">
            <div className="lg:sticky lg:top-28 lg:self-start">
              <span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#C41E3A]">Document readiness</span>
              <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight md:text-5xl">Build a clean seller file before portal entry.</h2>
              <p className="mt-5 text-sm leading-7 text-neutral-600">The final checklist changes by constitution, category and bid. These groups organise the information commonly reviewed during registration and participation.</p>
              <div className="mt-7 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-xs leading-6 text-amber-950"><strong>Important:</strong> portal fields, validations and buyer conditions can change. Confirm the live requirement before submission.</div>
            </div>
            <div className="grid gap-4 sm:grid-cols-2">
              {documentGroups.map(({ title, icon: Icon, items }) => (
                <article key={title} className="rounded-[1.75rem] border border-neutral-200 bg-white p-6 shadow-[0_18px_50px_-42px_rgba(15,23,42,0.4)]">
                  <div className="flex items-center gap-3"><span className="grid h-11 w-11 place-items-center rounded-2xl bg-rose-50 text-[#C41E3A]"><Icon className="h-5 w-5" /></span><h3 className="font-display text-lg font-extrabold">{title}</h3></div>
                  <ul className="mt-5 space-y-3">
                    {items.map((item) => <li key={item} className="flex gap-3 text-xs leading-5 text-neutral-600"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{item}</li>)}
                  </ul>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="process" className="overflow-hidden bg-[#0b1724] py-16 text-white lg:py-24">
          <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
            <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-7"><span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-emerald-300">Execution path</span><h2 className="mt-3 font-display text-3xl font-extrabold leading-tight md:text-5xl">An eight-stage GeM operating journey.</h2></div>
              <p className="text-sm leading-7 text-white/60 lg:col-span-5">The work continues after registration through assessment, catalogue development, opportunity screening, bid execution, order management and account maintenance.</p>
            </div>
            <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-4">
              {processSteps.map(({ icon: Icon, number, title, text }) => (
                <article key={number} className="group rounded-[1.5rem] border border-white/10 bg-white/[0.05] p-5 transition hover:-translate-y-1 hover:border-emerald-300/40 hover:bg-white/[0.08]">
                  <div className="flex items-center justify-between"><span className="font-display text-2xl font-black text-emerald-300">{number}</span><Icon className="h-5 w-5 text-white/50" /></div>
                  <h3 className="mt-7 font-display text-lg font-extrabold leading-snug">{title}</h3><p className="mt-3 text-xs leading-6 text-white/60">{text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="routes" className="mx-auto max-w-[1280px] px-6 py-16 lg:px-10 lg:py-24">
          <div className="text-center"><span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#C41E3A]">Choose the seller route</span><h2 className="mx-auto mt-3 max-w-3xl font-display text-3xl font-extrabold leading-tight md:text-5xl">Goods and services need different marketplace preparation.</h2></div>
          <div className="mt-10 grid gap-5 lg:grid-cols-2">
            <article className="relative overflow-hidden rounded-[2rem] border border-sky-200 bg-gradient-to-br from-white to-sky-50 p-7 md:p-9"><ShoppingBag className="h-9 w-9 text-sky-700" /><span className="mt-7 block text-[10px] font-extrabold uppercase tracking-[0.18em] text-sky-700">Product seller</span><h3 className="mt-2 font-display text-3xl font-extrabold">Catalogue-led readiness</h3><p className="mt-4 text-sm leading-7 text-neutral-600">Category selection, brand or OEM status, product specifications, images, commercial inputs and applicable certificates shape the product listing and bid route.</p></article>
            <article className="relative overflow-hidden rounded-[2rem] border border-emerald-200 bg-gradient-to-br from-white to-emerald-50 p-7 md:p-9"><Users className="h-9 w-9 text-emerald-700" /><span className="mt-7 block text-[10px] font-extrabold uppercase tracking-[0.18em] text-emerald-700">Service provider</span><h3 className="mt-2 font-display text-3xl font-extrabold">Capability-led readiness</h3><p className="mt-4 text-sm leading-7 text-neutral-600">Service scope, delivery model, workforce or experience evidence, commercial terms and buyer-specific eligibility drive the service offering and bid response.</p></article>
          </div>
        </section>

        <section id="engagement-models" className="border-y border-neutral-200 bg-white py-16 lg:py-24">
          <div className="mx-auto max-w-[1280px] px-6 lg:px-10">
            <div className="grid gap-8 lg:grid-cols-12 lg:items-end">
              <div className="lg:col-span-8"><span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#C41E3A]">Flexible engagement models</span><h2 className="mt-3 font-display text-3xl font-extrabold leading-tight md:text-5xl">Choose support that matches the stage and scale of your GeM operations.</h2></div>
              <p className="text-sm leading-7 text-neutral-600 lg:col-span-4">Professional fees and applicable government or assessment charges are scoped according to the selected services, portfolio and activity volume.</p>
            </div>
            <div className="mt-11 grid gap-5 lg:grid-cols-3">
              {engagementModels.map(({ icon: Icon, eyebrow, title, text, items, tone }) => (
                <article key={title} className={`rounded-[2rem] border p-7 transition duration-300 hover:-translate-y-1.5 hover:shadow-[0_26px_60px_-42px_rgba(15,23,42,0.5)] ${tone}`}>
                  <div className="flex items-center justify-between"><span className="grid h-14 w-14 place-items-center rounded-2xl bg-white text-[#C41E3A] shadow-sm"><Icon className="h-6 w-6" /></span><span className="rounded-full border border-neutral-200 bg-white/80 px-3 py-2 text-[9px] font-black uppercase tracking-[0.16em] text-neutral-500">{eyebrow}</span></div>
                  <h3 className="mt-8 font-display text-2xl font-extrabold leading-tight">{title}</h3><p className="mt-4 text-sm leading-7 text-neutral-600">{text}</p>
                  <div className="mt-6 space-y-2.5">{items.map((item) => <span key={item} className="flex items-start gap-2 text-xs font-semibold text-neutral-700"><CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />{item}</span>)}</div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="reporting" className="mx-auto max-w-[1280px] px-6 py-16 lg:px-10 lg:py-24">
          <div className="grid gap-12 lg:grid-cols-[1.04fr_0.96fr] lg:items-center">
            <div className="relative overflow-hidden rounded-[2.15rem] bg-[#0b1724] p-6 text-white shadow-2xl md:p-9">
              <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full border-[42px] border-emerald-400/[0.08]" />
              <div className="relative flex items-center justify-between"><div><span className="text-[10px] font-black uppercase tracking-[0.2em] text-emerald-300">Management reporting</span><h3 className="mt-2 font-display text-2xl font-extrabold md:text-3xl">A clear GeM activity dashboard.</h3></div><span className="grid h-12 w-12 place-items-center rounded-2xl bg-emerald-400/15 text-emerald-300"><BarChart3 className="h-5 w-5" /></span></div>
              <div className="relative mt-8 grid grid-cols-2 gap-3">
                {performanceMetrics.map(([label, Icon], index) => (
                  <div key={label} className="rounded-2xl border border-white/10 bg-white/[0.05] p-4 transition hover:border-emerald-300/30 hover:bg-white/[0.08]">
                    <div className="flex items-center justify-between"><span className="text-[10px] font-black text-white/35">0{index + 1}</span><Icon className="h-4 w-4 text-emerald-300" /></div><strong className="mt-5 block text-xs">{label}</strong>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#C41E3A]">Reporting, training & advisory</span>
              <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight md:text-5xl">Make the internal team stronger—not dependent on a black box.</h2>
              <p className="mt-5 text-sm leading-7 text-neutral-600">Depending on the engagement, regular reporting can track bids, orders, revenue, catalogue changes, account actions and the next decisions required.</p>
              <div className="mt-7 space-y-3">
                <div className="flex gap-4 rounded-2xl border border-neutral-200 bg-white p-5"><span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-emerald-50 text-emerald-700"><GraduationCap className="h-5 w-5" /></span><div><h3 className="font-display text-lg font-extrabold">Role-based team training</h3><p className="mt-2 text-xs leading-6 text-neutral-600">Training for sales, commercial, compliance and operations teams across account use, tenders, documentation, pricing and order workflows.</p></div></div>
                <div className="flex gap-4 rounded-2xl border border-neutral-200 bg-white p-5"><span className="grid h-11 w-11 shrink-0 place-items-center rounded-2xl bg-rose-50 text-[#C41E3A]"><MessagesSquare className="h-5 w-5" /></span><div><h3 className="font-display text-lg font-extrabold">Buyer and helpdesk coordination</h3><p className="mt-2 text-xs leading-6 text-neutral-600">Structured records and follow-up for buyer clarifications, order issues, complaints, disputes and GeM helpdesk interactions.</p></div></div>
              </div>
            </div>
          </div>
        </section>

        <section id="who-we-help" className="bg-gradient-to-br from-emerald-50/70 via-white to-amber-50/60 py-16 lg:py-24">
          <div className="mx-auto grid max-w-[1280px] gap-12 px-6 lg:grid-cols-[1.04fr_0.96fr] lg:items-center lg:px-10">
            <div>
              <span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-emerald-700">Who can benefit</span>
              <h2 className="mt-3 font-display text-3xl font-extrabold leading-tight md:text-5xl">GeM support for products, services and specialised government supply.</h2>
              <p className="mt-5 text-sm leading-7 text-neutral-600">The operating model can be adapted for first-time sellers, established manufacturers, growing MSMEs and teams managing active catalogues and procurement opportunities.</p>
              <div className="mt-7 flex flex-wrap gap-2">{beneficiaries.map((item) => <span key={item} className="rounded-full border border-emerald-200 bg-white px-3 py-2 text-[11px] font-bold text-neutral-700 shadow-sm">{item}</span>)}</div>
            </div>
            <div className="rounded-[2.1rem] border border-neutral-200 bg-white p-7 shadow-[0_28px_70px_-46px_rgba(5,150,105,0.45)] md:p-9">
              <span className="grid h-14 w-14 place-items-center rounded-2xl bg-emerald-50 text-emerald-700"><Handshake className="h-6 w-6" /></span>
              <span className="mt-8 block text-[10px] font-black uppercase tracking-[0.2em] text-[#C41E3A]">Why Complise India</span>
              <h3 className="mt-3 font-display text-3xl font-extrabold">One coordinated desk across the GeM lifecycle.</h3>
              <div className="mt-7 grid gap-3 sm:grid-cols-2">
                {['End-to-end support', 'Compliance expertise', 'Dedicated coordination', 'Structured reporting', 'Flexible engagement', 'Portal and document control'].map((item) => <div key={item} className="flex gap-2 rounded-xl bg-neutral-50 p-3 text-xs font-semibold text-neutral-700"><CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />{item}</div>)}
              </div>
            </div>
          </div>
        </section>

        <section id="faq" className="bg-white py-16 lg:py-24">
          <div className="mx-auto max-w-[1000px] px-6 lg:px-10">
            <div className="text-center"><span className="text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#C41E3A]">Common questions</span><h2 className="mt-3 font-display text-3xl font-extrabold md:text-5xl">GeM registration essentials.</h2></div>
            <div className="mt-10 grid gap-4">
              {faqs.map(([question, answer], index) => (
                <article key={question} className="grid gap-4 rounded-2xl border border-neutral-200 bg-[#FDFBF7] p-6 md:grid-cols-[48px_1fr]"><span className="grid h-10 w-10 place-items-center rounded-xl bg-white text-xs font-black text-[#C41E3A] shadow-sm">0{index + 1}</span><div><h3 className="font-display text-lg font-extrabold">{question}</h3><p className="mt-2 text-sm leading-7 text-neutral-600">{answer}</p></div></article>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1280px] px-6 py-16 lg:px-10 lg:py-24">
          <div className="relative overflow-hidden rounded-[2.25rem] bg-gradient-to-br from-[#9f1730] via-[#c41e3a] to-[#ee4965] p-8 text-white shadow-[0_30px_80px_-38px_rgba(196,30,58,0.8)] md:p-12">
            <div className="absolute -right-20 -top-24 h-80 w-80 rounded-full border-[48px] border-white/[0.08]" />
            <div className="relative grid gap-8 lg:grid-cols-[1fr_auto] lg:items-end"><div><span className="text-[10px] font-black uppercase tracking-[0.2em] text-white/70">Grow your government business through GeM</span><h2 className="mt-3 max-w-3xl font-display text-3xl font-extrabold leading-tight md:text-5xl">Prepare the account, catalogue, opportunity and order workflow together.</h2><p className="mt-4 max-w-2xl text-sm leading-7 text-white/75">Whether you are registering for the first time or need professional management of an active seller account, share your offering, current status and required support.</p></div><div className="flex flex-col gap-3"><a href="/contact" className="inline-flex h-[52px] items-center justify-center gap-3 rounded-full bg-white px-6 text-sm font-extrabold text-[#C41E3A] transition hover:-translate-y-1">Request GeM consultation <ArrowRight className="h-4 w-4" /></a><a href="/contact" className="inline-flex h-[52px] items-center justify-center gap-3 rounded-full border border-white/35 bg-white/10 px-6 text-sm font-extrabold text-white transition hover:-translate-y-1 hover:bg-white/20">Get a GeM service proposal <FileText className="h-4 w-4" /></a></div></div>
          </div>
        </section>

        <section className="mx-auto max-w-[1280px] px-6 pb-16 lg:px-10">
          <div className="flex flex-col justify-between gap-4 rounded-2xl border border-neutral-200 bg-white p-5 text-xs text-neutral-500 md:flex-row md:items-center"><span>GeM is a live government platform. Registration, catalogue and bid requirements should be confirmed on the official portal for the specific applicant and category.</span><a href="https://elearning.gem.gov.in/course/index.php?categoryid=2" target="_blank" rel="noopener noreferrer" className="inline-flex shrink-0 items-center gap-2 font-bold text-emerald-700">Official seller learning resources <FileText className="h-4 w-4" /></a></div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default GemServicePage;
